import { useState, useEffect } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GenieAvatar } from "@/components/GenieAvatar";
import { getAllGenieAvatars } from "@/data/genieDefaultImages";
import { cn } from "@/lib/utils";

interface GenieAvatarGallerySelectorProps {
  selectedImage?: string | null;
  onImageSelect: (imageUrl: string) => void;
  brandColor?: string;
  secondaryColor?: string | null;
  gradientEnabled?: boolean;
  gradientAngle?: number;
  size?: number;
  className?: string;
}

const GenieAvatarGallerySelector = ({
  selectedImage,
  onImageSelect,
  brandColor = "#4E9CFF",
  secondaryColor = null,
  gradientEnabled = false,
  gradientAngle = 135,
  size = 160,
  className,
}: GenieAvatarGallerySelectorProps) => {
  const availableImages = getAllGenieAvatars();

  // Compute initial selection synchronously
  const initialImage = selectedImage || availableImages[0] || '';

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [tempSelection, setTempSelection] = useState<string>(initialImage);

  // Ensure parent has initial value on mount
  useEffect(() => {
    if (!selectedImage && availableImages.length > 0) {
      onImageSelect(availableImages[0]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const currentImage = selectedImage || initialImage;

  const handleGalleryOpen = () => {
    setTempSelection(currentImage);
    setIsGalleryOpen(true);
  };

  const handleConfirmSelection = () => {
    if (tempSelection) {
      onImageSelect(tempSelection);
    }
    setIsGalleryOpen(false);
  };

  const handleCancel = () => {
    setTempSelection(currentImage);
    setIsGalleryOpen(false);
  };

  return (
    <>
      <div className={cn("flex flex-col items-center", className)}>
        {/* Large Preview */}
        <div className="mb-4">
          <GenieAvatar
            color={brandColor}
            secondaryColor={secondaryColor}
            gradientEnabled={gradientEnabled}
            gradientAngle={gradientAngle}
            size={size}
            customAvatarUrl={currentImage}
            speaking={false}
            listening={false}
            isConnecting={false}
            hasCustomBranding={true}
          />
        </div>

        {/* Change Avatar Button */}
        <Button
          onClick={handleGalleryOpen}
          variant="outline"
          className="px-6 py-2 border-2 hover:border-gray-400 transition-colors"
        >
          Change Avatar
        </Button>
      </div>

      {/* Gallery Dialog */}
      <Dialog open={isGalleryOpen} onOpenChange={setIsGalleryOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Select a Different Genie</DialogTitle>
          </DialogHeader>

          {/* Gallery Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 sm:gap-4 py-4">
            {availableImages.map((imageUrl, index) => {
              const isSelected = tempSelection === imageUrl;

              return (
                <button
                  key={imageUrl}
                  onClick={() => setTempSelection(imageUrl)}
                  className={cn(
                    "relative rounded-xl border-2 transition-all",
                    isSelected
                      ? "border-blue-500 shadow-xl ring-4 ring-blue-200 scale-105"
                      : "border-gray-200 hover:border-gray-300 hover:scale-105 hover:shadow-lg"
                  )}
                  aria-label={`Select genie avatar ${index + 1}`}
                >
                  <div className="aspect-square p-2">
                    <GenieAvatar
                      color={brandColor}
                      secondaryColor={secondaryColor}
                      gradientEnabled={gradientEnabled}
                      gradientAngle={gradientAngle}
                      size={80}
                      customAvatarUrl={imageUrl}
                      speaking={false}
                      listening={false}
                      isConnecting={false}
                      hasCustomBranding={true}
                    />
                  </div>

                  {/* Enhanced Selection Checkmark */}
                  {isSelected && (
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white">
                      <Check className="w-5 h-5 text-white" strokeWidth={3} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={handleCancel}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSelection}
              className="flex-1 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white hover:opacity-90"
            >
              Confirm Selection
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GenieAvatarGallerySelector;
